import { useEffect, useState } from 'react';

/**
 * Carga Google Maps JavaScript API con la bootstrap loader moderna y
 * luego importa la librería "places" (que incluye PlaceAutocompleteElement,
 * Place, etc.). Solo se carga cuando `enabled === true`.
 *
 * Referencia: https://developers.google.com/maps/documentation/javascript/load-maps-js-api
 */

let bootstrapPromise = null;

function injectBootstrap(apiKey) {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('No window'));
    }
    if (window.google?.maps?.importLibrary) {
        return Promise.resolve();
    }
    if (bootstrapPromise) {
        return bootstrapPromise;
    }
    if (!apiKey) {
        return Promise.reject(new Error('Missing REACT_APP_GOOGLE_PLACES_API_KEY'));
    }

    bootstrapPromise = new Promise((resolve, reject) => {
        // Bootstrap oficial de Google Maps JS API v3.
        // Define window.google.maps.importLibrary sin cargar todavía las librerías.
        (function (g) {
            // eslint-disable-next-line no-unused-expressions
            let h, a, k;
            const p = 'The Google Maps JavaScript API';
            const c = 'google';
            const l = 'importLibrary';
            const q = '__ib__';
            const m = document;
            let b = window;
            b = b[c] || (b[c] = {});
            const d = b.maps || (b.maps = {});
            const r = new Set();
            const e = new URLSearchParams();
            const u = () =>
                h ||
                (h = new Promise(async (f, n) => {
                    a = m.createElement('script');
                    e.set('libraries', [...r].join(','));
                    for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]);
                    e.set('callback', c + '.maps.' + q);
                    a.src = `https://maps.googleapis.com/maps/api/js?` + e;
                    d[q] = f;
                    a.onerror = () => (h = n(Error(p + ' could not load.')));
                    a.nonce = m.querySelector('script[nonce]')?.nonce || '';
                    m.head.append(a);
                }));
            d[l]
                ? console.warn(p + ' only loads once. Ignoring:', g)
                : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
        })({ key: apiKey, v: 'weekly' });

        // Esperamos a que importLibrary esté disponible.
        const start = Date.now();
        const interval = setInterval(() => {
            if (window.google?.maps?.importLibrary) {
                clearInterval(interval);
                resolve();
            } else if (Date.now() - start > 15000) {
                clearInterval(interval);
                bootstrapPromise = null;
                reject(new Error('Google Maps bootstrap timeout'));
            }
        }, 50);
    });

    return bootstrapPromise;
}

export default function useGoogleMapsPlaces(enabled) {
    const [placesLib, setPlacesLib] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!enabled) return;
        if (placesLib) return;

        const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
        let cancelled = false;

        (async () => {
            try {
                await injectBootstrap(apiKey);
                const lib = await window.google.maps.importLibrary('places');
                if (!cancelled) setPlacesLib(lib);
            } catch (err) {
                if (!cancelled) setError(err);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [enabled, placesLib]);

    return { placesLib, loaded: !!placesLib, error };
}
